import React, { useEffect } from 'react';
import { blockImplicitFormSubmitOnEnter } from '../../utils/formKeyHandlers';
import './ElegantModal.css';

/**
 * Diálogo modal reutilizable: fondo difuminado, panel con sombra y animación ligera.
 *
 * @param {boolean} show
 * @param {() => void} onClose
 * @param {string} title
 * @param {string} [subtitle]
 * @param {React.ReactNode} children — contenido principal (p. ej. formulario)
 * @param {React.ReactNode} [footer] — barra inferior opcional (acciones si no van dentro del children)
 * @param {'lg'|'xl'|'xxl'|'full'} [size='xl'] — ancho máximo aproximado
 * @param {boolean} [closeOnBackdrop=true] — clic fuera cierra
 * @param {boolean} [closeOnEscape] — Escape cierra (por defecto igual que dismissable / closeOnBackdrop)
 * @param {boolean} [dismissable=true] — si false, no cierra con clic fuera ni Escape (sí con botón X / Cancelar)
 * @param {boolean} [blockEnterSubmit=true] — Enter en inputs no dispara submit del formulario
 * @param {string} [titleId] — id para aria-labelledby
 */
function ElegantModal({
  show,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'xl',
  closeOnBackdrop = true,
  closeOnEscape,
  dismissable = true,
  blockEnterSubmit = true,
  titleId = 'elegant-modal-title',
}) {
  const allowBackdrop = dismissable && closeOnBackdrop;
  const allowEscape = dismissable && (closeOnEscape ?? closeOnBackdrop);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && allowEscape) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [show, onClose, allowEscape]);

  if (!show) return null;

  const maxWidths = { lg: 720, xl: 960, xxl: 1100, full: 'min(96vw, 1600px)' };
  const maxW = maxWidths[size] ?? maxWidths.xl;

  return (
    <div className="elegant-modal-root" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div
        className="elegant-modal-backdrop"
        onClick={() => allowBackdrop && onClose?.()}
        aria-hidden="true"
      />
      <div
        className={`elegant-modal-shell${size === 'full' ? ' elegant-modal-shell-full' : ''}`}
        style={{ maxWidth: maxW, width: size === 'full' ? '96vw' : undefined }}
      >
        <div className="elegant-modal-panel">
          <header className="elegant-modal-header">
            <div>
              <h2 className="elegant-modal-title" id={titleId}>
                {title}
              </h2>
              {subtitle ? <p className="elegant-modal-subtitle">{subtitle}</p> : null}
            </div>
            <button
              type="button"
              className="elegant-modal-close"
              aria-label="Cerrar"
              onClick={onClose}
            >
              <svg
                className="elegant-modal-close-icon"
                viewBox="0 0 14 14"
                width="14"
                height="14"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
                />
              </svg>
            </button>
          </header>
          <div
            className="elegant-modal-body"
            onKeyDown={blockEnterSubmit ? blockImplicitFormSubmitOnEnter : undefined}
          >
            {children}
          </div>
          {footer ? <footer className="elegant-modal-footer">{footer}</footer> : null}
        </div>
      </div>
    </div>
  );
}

export default ElegantModal;
